import React from 'react';
import { typography, textColors } from '../../../design-system/tokens';
import { 
  CheckCircleIcon, 
  ErrorIcon, 
  LoadingIcon,
  PendingIcon
} from './icons/AIIcons';

/**
 * Component to display a list of operation statuses with icons and messages
 * 
 * @param {Array} operations - Array of operation objects with status and description
 */
const OperationStatusList = ({ operations }) => {
  if (!operations || operations.length === 0) return null;
  
  /**
   * Get the appropriate icon for an operation status
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4 text-green-500 dark:text-green-400" />;
      case 'failed':
        return <ErrorIcon className="w-4 h-4 text-red-500 dark:text-red-400" />;
      case 'executing':
        return <LoadingIcon className="w-4 h-4 text-blue-500 dark:text-blue-400 animate-spin" />;
      case 'pending':
        return <PendingIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
      default:
        return <PendingIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
  };
  
  /**
   * Get human-readable status text
   */
  const getStatusText = (status) => {
    switch (status) {
      case 'completed': 
        return 'Completed';
      case 'failed': 
        return 'Failed';
      case 'executing': 
        return 'In progress...';
      case 'pending': 
        return 'Pending...';
      default: 
        return status;
    }
  };
  
  /**
   * Get text color based on status
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      case 'executing':
        return 'text-blue-600 dark:text-blue-400';
      case 'pending':
        return `${textColors.muted}`;
      default:
        return `${textColors.muted}`;
    }
  };
  
  return (
    <div className="mt-2 space-y-1.5 ai-op-status-list">
      {operations.map((op, index) => (
        <div 
          key={op.id || index}
          className="flex items-start gap-2 text-sm"
        >
          {/* Status Icon */}
          <div className="mt-0.5 flex-shrink-0">
            {getStatusIcon(op.execution_status)}
          </div>
          
          {/* Operation Description */}
          <div className="flex-1 min-w-0">
            <p className={`${typography.bodySmall} ${textColors.primary}`}>
              {op.description}
            </p>
            
            {/* Error Message (if any) */}
            {op.error_message && (
              <p className={`${typography.caption} text-red-600 dark:text-red-400 mt-0.5`}>
                Error: {op.error_message}
              </p>
            )}
          </div>
          
          {/* Status Text */}
          <span 
            className={`${typography.caption} flex-shrink-0 ${getStatusColor(op.execution_status)} ${
              op.execution_status === "completed" ? "ai-op-status-text" : ""
            }`}
            title={getStatusText(op.execution_status)}
          >
            {getStatusText(op.execution_status)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default OperationStatusList;

